import React, { useEffect, useState } from "react";
import { DatePicker, Empty, Modal, Tooltip } from "antd";
import type { DatePickerProps } from "antd";
import {
  PhoneOutlined,
  MessageOutlined,
  MailOutlined,
  WhatsAppOutlined,
  PlayCircleOutlined,
  ClockCircleOutlined,
  RightOutlined,
  NotificationOutlined,
  UserSwitchOutlined,
  DownOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import styled, { css } from "styled-components";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store";
import { getDashboardDataThunk } from "../../store/actions/dashboard.action";
import { CAMPAIGN_STATUS } from "../outbound/type";
import { formatNumber } from "../../utils/formatDecimalNumber";
import theme from "../../themes";
import ContentLoading, { filterControlCss } from "../../components/ContentLoading";
import {
  kpiBreakCss,
  pageShellCss,
  pageTitleCss,
  wrapRowCss,
} from "../../styles/responsive";
import { useChannelSettings } from "../../hooks/useChannelSettings";

type DashScope = "All" | "Inbound" | "Outbound";

const CHANNEL_COLORS = {
  call: "rgb(16,148,120)",
  sms: "rgb(253,196,72)",
  whatsapp: "rgb(0,203,160)",
  email: "rgb(156,163,175)",
};

const STATUS_COLORS = {
  completed: "rgb(16,148,120)",
  escalated: "rgb(253,196,72)",
  failed: "rgb(255,0,0)",
  open: theme.colors.blue1,
};

const channelIcon = (key: string) => {
  if (key === "call") return <PhoneOutlined />;
  if (key === "sms") return <MessageOutlined />;
  if (key === "whatsapp") return <WhatsAppOutlined />;
  return <MailOutlined />;
};

const normalizeChannel = (value?: string) => {
  const raw = String(value || "call").toLowerCase();
  if (raw.includes("whatsapp") || raw === "forum") return "whatsapp";
  if (raw.includes("mail") || raw.includes("email")) return "email";
  if (raw.includes("sms") || raw.includes("text")) return "sms";
  return "call";
};

const DEFAULT_PROVISIONED_MESSAGES = 10000;
const CHANNEL_SUPPORT_MAIL = "it-support@lytte.ai";

const isRequestableChannel = (key: string) => key === "sms" || key === "whatsapp";

type ChannelStats = {
  totalTickets: number;
  totalMessages: number;
  inboundMessages: number;
  outboundMessages: number;
  closedTickets: number;
  failedTickets: number;
  needsReplyCount: number;
  openTickets: number;
};

const asCount = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const billedMinutes = (value: unknown) => Math.ceil(Math.max(0, asCount(value)));

const calendarMonthRange = (month: dayjs.Dayjs) => {
  const year = month.year();
  const monthIndex = month.month();
  const start = new Date(Date.UTC(year, monthIndex, 1));
  const end = new Date(Date.UTC(year, monthIndex + 1, 1) - 1);
  return {
    start_time: start.toISOString(),
    end_time: end.toISOString(),
  };
};

const usageTone = (share: number, hasCap: boolean) => {
  if (!hasCap) {
    return { bar: theme.colors.green1, text: theme.colors.grey1 };
  }
  if (share > 50) {
    return { bar: theme.colors.yellow1, text: theme.colors.yellow4 };
  }
  return { bar: theme.colors.green1, text: theme.colors.green1 };
};

const channelStatsOf = (raw?: Partial<ChannelStats> | null): ChannelStats => {
  const totalTickets = asCount(raw?.totalTickets);
  const closedTickets = asCount(raw?.closedTickets);
  const failedTickets = asCount(raw?.failedTickets);
  const needsReplyCount = asCount(raw?.needsReplyCount);
  const openFromApi = raw?.openTickets;
  return {
    totalTickets,
    totalMessages: asCount(raw?.totalMessages),
    inboundMessages: asCount(raw?.inboundMessages),
    outboundMessages: asCount(raw?.outboundMessages),
    closedTickets,
    failedTickets,
    needsReplyCount,
    openTickets:
      openFromApi == null
        ? Math.max(0, totalTickets - closedTickets - failedTickets - needsReplyCount)
        : asCount(openFromApi),
  };
};

const consumedUsed = (channel: ChannelStats) =>
  Math.max(
    0,
    asCount(channel.totalMessages) ||
      asCount(channel.inboundMessages) + asCount(channel.outboundMessages)
  );

const CHANNEL_ROW_GAP = 24;
const CHANNEL_ROW_HEIGHT = 20;
const CHART_VISUAL_SIZE = 168;
const BAR_HEIGHT = 8;
const BAR_TRACK_BG = "#D1D5DB";

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [scope, setScope] = useState<DashScope>("All");
  const [selectedMonth, setSelectedMonth] = useState(() => dayjs().startOf("month"));

  const { data, loading } = useAppSelector((state) => state.dashboard);
  const { user } = useAppSelector((state) => state.auth);
  const { isEnabled } = useChannelSettings();
  const showChannel = (key: string) => isEnabled(key) || isRequestableChannel(key);
  const channelLocked = (key: string) => isRequestableChannel(key) && !isEnabled(key);

  useEffect(() => {
    dispatch(getDashboardDataThunk(calendarMonthRange(selectedMonth)));
  }, [dispatch, selectedMonth]);

  const inboundCalls = isEnabled("call") ? (data?.inbound?.count ?? 0) : 0;
  const outboundCalls = isEnabled("call") ? (data?.outbound?.count ?? 0) : 0;
  const totalCalls = isEnabled("call")
    ? (data?.number_of_calls ?? inboundCalls + outboundCalls)
    : 0;
  const smsStats = isEnabled("sms") ? channelStatsOf(data?.sms) : channelStatsOf();
  const waStats = isEnabled("whatsapp")
    ? channelStatsOf(data?.whatsapp)
    : channelStatsOf();
  const emailStats = isEnabled("email")
    ? channelStatsOf(data?.email)
    : channelStatsOf();
  const waMessages = waStats.totalMessages;
  const waTickets = waStats.totalTickets;
  const needsReply =
    smsStats.needsReplyCount + waStats.needsReplyCount + emailStats.needsReplyCount;
  const closedTickets =
    smsStats.closedTickets + waStats.closedTickets + emailStats.closedTickets;
  const failedTickets =
    smsStats.failedTickets + waStats.failedTickets + emailStats.failedTickets;
  const openTickets =
    smsStats.openTickets + waStats.openTickets + emailStats.openTickets;

  const usageCards = [
    {
      key: "call",
      label: t("dashboard.v2.totalCalls"),
      value: formatNumber(totalCalls),
      note: t("dashboard.v2.inboundOutboundNote", {
        inbound: formatNumber(inboundCalls),
        outbound: formatNumber(outboundCalls),
      }),
    },
    {
      key: "sms",
      label: t("dashboard.v2.totalMessages"),
      value: formatNumber(smsStats.totalMessages),
      note: t("dashboard.v2.inboundOutboundNote", {
        inbound: formatNumber(smsStats.inboundMessages),
        outbound: formatNumber(smsStats.outboundMessages),
      }),
    },
    {
      key: "whatsapp",
      label: t("dashboard.v2.totalWhatsapp"),
      value: formatNumber(waMessages),
      note: t("dashboard.v2.ticketNote", { count: waTickets }),
    },
    {
      key: "email",
      label: t("dashboard.v2.totalEmails"),
      value: formatNumber(emailStats.totalMessages),
      note: t("dashboard.v2.inboundOutboundNote", {
        inbound: formatNumber(emailStats.inboundMessages),
        outbound: formatNumber(emailStats.outboundMessages),
      }),
    },
  ].filter((card) => showChannel(card.key));

  const minutesUsed = billedMinutes(data?.total_minutes);
  const minutesCap = asCount(data?.minutes_provisioned) || 10000;
  const smsCap = asCount(data?.provisioned_sms) || DEFAULT_PROVISIONED_MESSAGES;
  const whatsappCap =
    asCount(data?.provisioned_whatsapp) || DEFAULT_PROVISIONED_MESSAGES;
  const isCurrentMonth = selectedMonth.isSame(dayjs(), "month");
  const consBars = [
    {
      key: "call",
      label: t("dashboard.v2.callMinutes"),
      used: minutesUsed,
      cap: minutesCap,
      unit: t("dashboard.v2.min"),
    },
    {
      key: "sms",
      label: t("dashboard.v2.textMessages"),
      used: consumedUsed(smsStats),
      cap: smsCap,
      unit: t("dashboard.v2.messages"),
    },
    {
      key: "whatsapp",
      label: t("dashboard.v2.whatsappMessages"),
      used: consumedUsed(waStats),
      cap: whatsappCap,
      unit: t("dashboard.v2.messages"),
    },
    {
      key: "email",
      label: t("dashboard.v2.emails"),
      used: consumedUsed(emailStats),
      cap: 0,
      unit: t("dashboard.v2.emailsUnit"),
    },
  ].map((row) => {
    const hasCap = row.cap > 0;
    const share = hasCap
      ? Math.round((row.used / Math.max(row.cap, 1)) * 100)
      : 0;
    const tone = usageTone(share, hasCap);
    return {
      ...row,
      hasCap,
      atLimit: isCurrentMonth && hasCap && share >= 100,
      width: `${Math.min(100, Math.max(0, share))}%`,
      share: hasCap ? `${share}%` : row.used ? "—" : "0%",
      tone,
      usedLabel: hasCap
        ? t("dashboard.v2.usedOf", {
            used: formatNumber(row.used),
            cap: formatNumber(row.cap),
            unit: row.unit,
          })
        : `${formatNumber(row.used)} ${row.unit}`,
    };
  }).filter((row) => showChannel(row.key));

  const inboundCompleted = isEnabled("call") ? (data?.inbound?.completed ?? 0) : 0;
  const inboundFailed = isEnabled("call") ? (data?.inbound?.failed ?? 0) : 0;
  const inboundOpen = isEnabled("call") ? (data?.inbound?.in_progress ?? 0) : 0;
  const conversationTotal =
    inboundCalls +
    (isEnabled("sms") ? smsStats.totalTickets : 0) +
    (isEnabled("whatsapp") ? waTickets : 0) +
    (isEnabled("email") ? emailStats.totalTickets : 0);

  const statusKpis = [
    {
      key: "open",
      label: t("dashboard.v2.open"),
      color: STATUS_COLORS.open,
      value: formatNumber(openTickets + inboundOpen),
      note: t("dashboard.v2.percentOfConversations", {
        percent: conversationTotal
          ? Math.round(((openTickets + inboundOpen) / conversationTotal) * 100)
          : 0,
        total: formatNumber(conversationTotal),
      }),
    },
    {
      key: "completed",
      label: t("dashboard.v2.completed"),
      color: STATUS_COLORS.completed,
        value: formatNumber(closedTickets + inboundCompleted),
        note: t("dashboard.v2.percentOfConversations", {
          percent: conversationTotal
            ? Math.round(((closedTickets + inboundCompleted) / conversationTotal) * 100)
            : 0,
        total: formatNumber(conversationTotal),
      }),
    },
    {
      key: "failed",
      label: t("dashboard.v2.failed"),
      color: STATUS_COLORS.failed,
      value: formatNumber(failedTickets + inboundFailed),
      note: t("dashboard.v2.percentOfConversations", {
        percent: conversationTotal
          ? Math.round(((failedTickets + inboundFailed) / conversationTotal) * 100)
          : 0,
        total: formatNumber(conversationTotal),
      }),
    },
    {
      key: "needsReply",
      label: t("inbound.whatsapp.needsReply"),
      color: STATUS_COLORS.escalated,
      value: formatNumber(needsReply),
      note: t("dashboard.v2.percentOfConversations", {
        percent: conversationTotal
          ? Math.round((needsReply / Math.max(conversationTotal, 1)) * 100)
          : 0,
        total: formatNumber(conversationTotal),
      }),
    },
  ];

  const statusSegments = (
    total: number,
    parts: { key: string; count: number; color: string }[]
  ) =>
    total
      ? parts
          .filter((part) => part.count > 0)
          .map((part) => ({
            key: part.key,
            width: `${Math.round((part.count / total) * 100)}%`,
            color: part.color,
          }))
      : [];

  const messagingParts = (channel: ChannelStats) => [
    { key: t("dashboard.v2.completed"), count: channel.closedTickets, color: STATUS_COLORS.completed },
    { key: t("inbound.whatsapp.needsReply"), count: channel.needsReplyCount, color: STATUS_COLORS.escalated },
    { key: t("dashboard.v2.failed"), count: channel.failedTickets, color: STATUS_COLORS.failed },
    { key: t("dashboard.v2.open"), count: channel.openTickets, color: STATUS_COLORS.open },
  ];

  const statusRows = [
    {
      key: "call",
      label: t("dashboard.v2.phoneCall"),
      total: `${formatNumber(inboundCalls)} ${t("dashboard.v2.calls")}`,
      opacity: inboundCalls ? 1 : 0.45,
      segments: statusSegments(inboundCalls, [
        { key: t("dashboard.v2.completed"), count: inboundCompleted, color: STATUS_COLORS.completed },
        { key: t("dashboard.v2.failed"), count: inboundFailed, color: STATUS_COLORS.failed },
        { key: t("dashboard.v2.open"), count: inboundOpen, color: STATUS_COLORS.open },
      ]),
    },
    {
      key: "whatsapp",
      label: t("dashboard.v2.whatsapp"),
      total: `${formatNumber(waTickets)} ${t("dashboard.v2.conversations")}`,
      opacity: waTickets ? 1 : 0.45,
      segments: statusSegments(waTickets, messagingParts(waStats)),
    },
    {
      key: "sms",
      label: t("dashboard.v2.textMessage"),
      total: `${formatNumber(smsStats.totalTickets)} ${t("dashboard.v2.conversations")}`,
      opacity: smsStats.totalTickets ? 1 : 0.45,
      segments: statusSegments(smsStats.totalTickets, messagingParts(smsStats)),
    },
    {
      key: "email",
      label: t("dashboard.v2.email"),
      total: `${formatNumber(emailStats.totalTickets)} ${t("dashboard.v2.conversations")}`,
      opacity: emailStats.totalTickets ? 1 : 0.45,
      segments: statusSegments(emailStats.totalTickets, messagingParts(emailStats)),
    },
  ].filter((row) => showChannel(row.key));

  const campaignSummary = data?.campaigns;
  const campChannelCounts = {
    call: asCount(campaignSummary?.by_channel?.call?.count),
    sms: asCount(campaignSummary?.by_channel?.sms?.count),
    whatsapp: asCount(campaignSummary?.by_channel?.whatsapp?.count),
    email: asCount(campaignSummary?.by_channel?.email?.count),
  };
  const campSends = {
    call: asCount(campaignSummary?.by_channel?.call?.sends),
    sms: asCount(campaignSummary?.by_channel?.sms?.sends),
    whatsapp: asCount(campaignSummary?.by_channel?.whatsapp?.sends),
    email: asCount(campaignSummary?.by_channel?.email?.sends),
  };
  const running = asCount(campaignSummary?.running);
  const scheduled = asCount(campaignSummary?.scheduled);
  const finished = asCount(campaignSummary?.finished);

  const campKpis = [
    {
      icon: <NotificationOutlined />,
      label: t("dashboard.v2.totalCampaigns"),
      value: formatNumber(campaignSummary?.total || 0),
      note: t("dashboard.v2.finishedInRange", { count: finished }),
    },
    {
      icon: <PlayCircleOutlined />,
      label: t("dashboard.v2.activeCampaigns"),
      value: formatNumber(running),
      note: t("dashboard.v2.sendingNow"),
    },
    {
      icon: <ClockCircleOutlined />,
      label: t("dashboard.v2.scheduledCampaigns"),
      value: formatNumber(scheduled),
      note: t("dashboard.v2.queuedToStart"),
    },
  ];

  const campByChannel = [
    { key: "call", label: t("dashboard.v2.phoneCall"), color: CHANNEL_COLORS.call, count: campChannelCounts.call },
    { key: "sms", label: t("dashboard.v2.textMessage"), color: CHANNEL_COLORS.sms, count: campChannelCounts.sms },
    { key: "whatsapp", label: t("dashboard.v2.whatsapp"), color: CHANNEL_COLORS.whatsapp, count: campChannelCounts.whatsapp },
    { key: "email", label: t("dashboard.v2.email"), color: CHANNEL_COLORS.email, count: campChannelCounts.email },
  ].filter((row) => showChannel(row.key));
  const campTotal = Math.max(
    1,
    campByChannel.reduce((sum, row) => sum + row.count, 0)
  );
  const campPie = (() => {
    let at = 0;
    const stops = campByChannel
      .filter((row) => row.count > 0)
      .map((row) => {
        const from = at;
        const to = at + (row.count / campTotal) * 100;
        at = to;
        return `${row.color} ${from.toFixed(1)}% ${to.toFixed(1)}%`;
      });
    if (!stops.length) return "conic-gradient(rgb(229,231,235) 0% 100%)";
    return `conic-gradient(${stops.join(",")})`;
  })();

  const maxSends = Math.max(1, ...Object.values(campSends));
  const sendsByChannel = campByChannel.map((row) => ({
    ...row,
    sends: campSends[row.key as keyof typeof campSends],
    width: `${Math.round((campSends[row.key as keyof typeof campSends] / maxSends) * 100)}%`,
  }));
  const totalSends = sendsByChannel.reduce((sum, row) => sum + row.sends, 0);

  const campaignStatusLabel = (status?: string) => {
    if (status === CAMPAIGN_STATUS.IN_PROGRESS) {
      return t("dashboard.v2.campaignStatusRunning");
    }
    if (status === CAMPAIGN_STATUS.COMPLETED) {
      return t("dashboard.v2.campaignStatusFinished");
    }
    if (status === CAMPAIGN_STATUS.SCHEDULE) {
      return t("dashboard.v2.campaignStatusScheduled");
    }
    if (status === CAMPAIGN_STATUS.STOPPED) {
      return t("dashboard.v2.campaignStatusStopped");
    }
    if (status === CAMPAIGN_STATUS.TEST) {
      return t("dashboard.v2.campaignStatusTesting");
    }
    return status || t("dashboard.v2.followUpNote");
  };

  const followUps = (data?.outbound?.need_review_campaigns || [])
    .slice(0, 4)
    .map((c: any, index: number, arr: any[]) => ({
      id: c._id,
      channel: c.channel,
      icon: channelIcon(normalizeChannel(c.channel)),
      label: c.campaign_name,
      note: t("dashboard.v2.followUpNote", {
        status: campaignStatusLabel(c.status),
      }),
      value: formatNumber(c.need_review_count || 0),
      last: index === arr.length - 1,
    }));

  const inboundAttention = [
    {
      icon: <UserSwitchOutlined />,
      label: t("inbound.whatsapp.needsReply"),
      note: t("dashboard.v2.escalatedNote"),
      value: formatNumber(needsReply),
      to: "/inbound?status=escalated",
    },
    {
      icon: <ClockCircleOutlined />,
      label: t("dashboard.v2.openOver24h"),
      note: t("dashboard.v2.openOver24hNote"),
      value: formatNumber(openTickets),
      to: "/inbound",
    },
  ];

  const busy = loading;

  return (
    <Page>
      <Header>
        <Title>{t("dashboard.title")}</Title>
        <HeaderControls>
          <ScopeGroup>
            {(["All", "Inbound", "Outbound"] as DashScope[]).map((item, index) => (
              <ScopeButton
                key={item}
                type="button"
                $active={scope === item}
                $first={index === 0}
                onClick={() => setScope(item)}
              >
                {busy && scope === item ? <LoadingOutlined /> : null}
                {item === "All"
                  ? t("dashboard.v2.all")
                  : item === "Inbound"
                    ? t("dashboard.v2.inbound")
                    : t("dashboard.v2.outbound")}
              </ScopeButton>
            ))}
          </ScopeGroup>
          <MonthPicker
            picker="month"
            value={selectedMonth}
            onChange={(value) => {
              if (dayjs.isDayjs(value)) {
                setSelectedMonth(value.startOf("month"));
              }
            }}
            format="MMMM YYYY"
            allowClear={false}
            suffixIcon={busy ? <LoadingOutlined /> : <DownOutlined />}
            disabledDate={(current) =>
              Boolean(dayjs.isDayjs(current) && current.isAfter(dayjs(), "month"))
            }
          />
          {user?.is_super_admin && (
            <SwitchCompany type="button" onClick={() => navigate("/company-selection")}>
              {t("dashboard.switchCompany")}
            </SwitchCompany>
          )}
        </HeaderControls>
      </Header>

      <ContentLoading spinning={busy}>
      {scope === "All" && (
        <Stack>
          <SectionLabel>{t("dashboard.v2.usage")}</SectionLabel>
          <KpiGrid $cols={Math.max(usageCards.length, 1)}>
            {usageCards.map((card) => (
              <ChannelLock
                key={card.key}
                locked={channelLocked(card.key)}
                channelLabel={card.label}
              >
                <KpiCard>
                  <KpiLabel>
                    {channelIcon(card.key)}
                    {card.label}
                  </KpiLabel>
                  <KpiValue>{card.value}</KpiValue>
                  <KpiNote>{card.note}</KpiNote>
                </KpiCard>
              </ChannelLock>
            ))}
          </KpiGrid>
          <Panel>
            <PanelHead>
              <PanelTitle>{t("dashboard.v2.consumedByChannel")}</PanelTitle>
              <PanelHint>{t("dashboard.v2.consumedHint")}</PanelHint>
            </PanelHead>
            <MetricBarList>
              {consBars.map((bar) => (
                <ChannelLock
                  key={bar.key}
                  locked={channelLocked(bar.key)}
                  channelLabel={bar.label}
                  row
                  action={false}
                >
                  <BarLabel>
                    {channelIcon(bar.key)}
                    {bar.label}
                  </BarLabel>
                  <BarTrack>
                    {bar.atLimit ? (
                      <Tooltip title={t("dashboard.v2.overLimitLabel")}>
                        <Track $h={BAR_HEIGHT} $borderless $interactive>
                          <Fill $width={bar.width} $color={bar.tone.bar} />
                        </Track>
                      </Tooltip>
                    ) : (
                      <Track $h={BAR_HEIGHT} $borderless>
                        <Fill $width={bar.width} $color={bar.tone.bar} />
                      </Track>
                    )}
                  </BarTrack>
                  <BarMeta>
                    <strong>{bar.share}</strong>
                    <span>({bar.usedLabel})</span>
                  </BarMeta>
                </ChannelLock>
              ))}
            </MetricBarList>
            <Footnote>{t("dashboard.v2.consumedNote")}</Footnote>
          </Panel>
        </Stack>
      )}

      {scope === "Inbound" && (
        <Stack>
          <SectionLabel>{t("dashboard.v2.inboundByStatus")}</SectionLabel>
          <KpiGrid $cols={4}>
            {statusKpis.map((card) => (
              <KpiCard key={card.key}>
                <KpiLabel>
                  <Dot $color={card.color} />
                  {card.label}
                </KpiLabel>
                <KpiValue>{card.value}</KpiValue>
                <KpiNote>{card.note}</KpiNote>
              </KpiCard>
            ))}
          </KpiGrid>
          <Panel>
            <PanelCopy>
              <PanelTitle>{t("dashboard.v2.distributionByChannel")}</PanelTitle>
              <PanelHint>{t("dashboard.v2.distributionHint")}</PanelHint>
            </PanelCopy>
            <DistList>
              {statusRows.map((row) => (
                <ChannelLock
                  key={row.key}
                  locked={channelLocked(row.key)}
                  channelLabel={row.label}
                  action={false}
                >
                  <DistRow $opacity={channelLocked(row.key) ? 1 : row.opacity}>
                    <DistHead>
                      <BarLabel>
                        {channelIcon(row.key)}
                        {row.label}
                      </BarLabel>
                      <KpiNote>{row.total}</KpiNote>
                    </DistHead>
                    <Stacked>
                      {row.segments.map((segment) => (
                        <StackSeg
                          key={segment.key}
                          $width={segment.width}
                          $color={segment.color}
                          title={`${segment.key} ${segment.width}`}
                        />
                      ))}
                    </Stacked>
                    <Legend>
                      {row.segments.map((segment) => (
                        <LegendItem key={segment.key}>
                          <Dot $color={segment.color} />
                          {segment.key} {segment.width}
                        </LegendItem>
                      ))}
                    </Legend>
                  </DistRow>
                </ChannelLock>
              ))}
            </DistList>
          </Panel>
          <Panel>
            <PanelHead>
              <PanelTitle>{t("dashboard.v2.inboundNeedsAttention")}</PanelTitle>
              <TextLink type="button" onClick={() => navigate("/inbound")}>
                {t("dashboard.v2.viewInbox")}
              </TextLink>
            </PanelHead>
            {inboundAttention.map((item, index) => (
              <AttentionRow
                key={item.label}
                $last={index === inboundAttention.length - 1}
                onClick={() => navigate(item.to)}
              >
                {item.icon}
                <AttentionCopy>
                  <span>{item.label}</span>
                  <KpiNote>{item.note}</KpiNote>
                </AttentionCopy>
                <AttentionValue>{item.value}</AttentionValue>
                <RightOutlined />
              </AttentionRow>
            ))}
          </Panel>
        </Stack>
      )}

      {scope === "Outbound" && (
        <Stack>
          <KpiGrid $cols={3}>
            {campKpis.map((card) => (
              <KpiCard key={card.label}>
                <KpiLabel>
                  {card.icon}
                  {card.label}
                </KpiLabel>
                <KpiValue>{card.value}</KpiValue>
                <KpiNote>{card.note}</KpiNote>
              </KpiCard>
            ))}
          </KpiGrid>
          <TwoCol>
            <Panel>
              <PanelCopy>
                <PanelTitle>{t("dashboard.v2.campaignsByChannel")}</PanelTitle>
                <PanelHint>{t("dashboard.v2.campaignsByChannelHint")}</PanelHint>
              </PanelCopy>
              <ChartVisual>
                <Pie $bg={campPie} />
              </ChartVisual>
              <PieLegend>
                {campByChannel.map((row) => (
                  <ChannelLock
                    key={row.key}
                    locked={channelLocked(row.key)}
                    channelLabel={row.label}
                  >
                    <LegendLine>
                      <Dot $color={row.color} />
                      <span>{row.label}</span>
                      <Muted>
                        {row.count}{" "}
                        {row.count === 1
                          ? t("dashboard.v2.campaign")
                          : t("dashboard.v2.campaigns")}
                      </Muted>
                      <strong>{Math.round((row.count / campTotal) * 100)}%</strong>
                    </LegendLine>
                  </ChannelLock>
                ))}
              </PieLegend>
            </Panel>
            <Panel>
              <PanelCopy>
                <PanelTitle>{t("dashboard.v2.totalSendsByChannel")}</PanelTitle>
                <PanelHint>{t("dashboard.v2.totalSendsHint")}</PanelHint>
              </PanelCopy>
              <ChartVisual>
                <SendHero>
                  <SendHeroValue>{formatNumber(totalSends)}</SendHeroValue>
                  <KpiNote>{t("dashboard.v2.sends")}</KpiNote>
                </SendHero>
              </ChartVisual>
              <MetricBarList>
                {sendsByChannel.map((row) => (
                  <ChannelLock
                    key={row.key}
                    locked={channelLocked(row.key)}
                    channelLabel={row.label}
                    row
                    action={false}
                  >
                    <BarLabel>
                      {channelIcon(row.key)}
                      {row.label}
                    </BarLabel>
                    <BarTrack>
                      <Track $h={BAR_HEIGHT} $borderless>
                        <Fill $width={row.width || "0%"} $color={row.color} />
                      </Track>
                    </BarTrack>
                    <SendValue>
                      {formatNumber(row.sends)} {t("dashboard.v2.sends")}
                    </SendValue>
                  </ChannelLock>
                ))}
              </MetricBarList>
            </Panel>
          </TwoCol>
          <Panel>
            <PanelCopy>
              <PanelHead>
                <PanelTitle>{t("dashboard.v2.outboundNeedsAttention")}</PanelTitle>
                <TextLink type="button" onClick={() => navigate("/outbound")}>
                  {t("dashboard.v2.viewCampaigns")}
                </TextLink>
              </PanelHead>
              <PanelHint>{t("dashboard.v2.outboundNeedsHint")}</PanelHint>
            </PanelCopy>
            {followUps.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("common.noData")}
              />
            ) : (
              followUps.map((item) => (
                <AttentionRow
                  key={item.id}
                  $last={item.last}
                  onClick={() =>
                    navigate(`/outbound/${item.id}`, {
                      state: { channel: item.channel },
                    })
                  }
                >
                  {item.icon}
                  <AttentionCopy>
                    <span>{item.label}</span>
                    <KpiNote>{item.note}</KpiNote>
                  </AttentionCopy>
                  <AttentionValue>{item.value}</AttentionValue>
                  <RightOutlined />
                </AttentionRow>
              ))
            )}
          </Panel>
        </Stack>
      )}
      </ContentLoading>
    </Page>
  );
};

const Page = styled.div`
  background: ${theme.colors.grey3};
  display: flex;
  flex-direction: column;
  gap: 26px;
  ${pageShellCss}
`;

const Header = styled.div`
  ${wrapRowCss}
  min-height: 48px;
`;

const Title = styled.h1`
  margin: 0;
  font-weight: 600;
  font-size: 24px;
  line-height: 1;
  color: #000;
  ${pageTitleCss}
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const ScopeGroup = styled.div`
  display: flex;
  height: 42px;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  overflow: hidden;
  background: #fff;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const ScopeButton = styled.button<{ $active: boolean; $first: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  padding: 0 16px;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  border: none;
  border-left: 1px solid
    ${({ $first }) => ($first ? "transparent" : theme.colors.grey2)};
  background: ${({ $active }) => ($active ? theme.colors.green1 : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#000")};
  ${filterControlCss}

  @media (max-width: 640px) {
    flex: 1;
    justify-content: center;
    padding: 0 10px;
    font-size: 14px;
  }
`;

const MonthPicker = styled(DatePicker)<DatePickerProps>`
  && {
    width: 220px;
    height: 42px !important;
    min-height: 42px !important;
    border-radius: 8px !important;
    border-color: ${theme.colors.grey2} !important;
    padding: 0 14px !important;
  }

  @media (max-width: 640px) {
    && {
      width: 100%;
      flex: 1 1 100%;
    }
  }

  .ant-picker-input > input {
    font-size: 16px;
    line-height: 1;
  }
`;

const SwitchCompany = styled.button`
  display: flex;
  align-items: center;
  height: 42px;
  padding: 0 16px;
  border: none;
  border-radius: 8px;
  background: ${theme.colors.green1};
  color: #fff;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;
`;

const SectionLabel = styled.span`
  font-weight: 600;
  font-size: 16px;
  line-height: 1;
  color: #000;
`;

const KpiGrid = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols}, 1fr);
  gap: 26px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
  }

  ${kpiBreakCss}
`;

const KpiCard = styled.div`
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.09);
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const KpiLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 20px;
  font-size: 16px;
  line-height: 1.25;
  color: ${theme.colors.grey1};

  .anticon {
    color: inherit;
    font-size: 20px;
  }
`;

const KpiValue = styled.span`
  font-weight: 700;
  font-size: 32px;
  line-height: 1;
  color: #000;

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const KpiNote = styled.span`
  font-size: 14px;
  line-height: 1.4;
  color: ${theme.colors.grey1};
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.09);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const PanelHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const PanelCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-height: 44px;
`;

const PanelTitle = styled.span`
  font-weight: 600;
  font-size: 16px;
  line-height: 1;
  color: #000;
`;

const PanelHint = styled.span`
  font-size: 14px;
  line-height: 1.4;
  color: ${theme.colors.grey1};
`;

const BarLabel = styled.span`
  width: 172px;
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 16px;
  line-height: 1;
  color: #000;
  flex: none;

  @media (max-width: 640px) {
    width: 100%;
  }

  .anticon {
    font-size: 20px;
    color: rgb(31, 31, 31);
  }
`;

const BarTrack = styled.div`
  min-width: 0;
  width: 100%;

  > * {
    display: block;
    width: 100%;
  }
`;

const Track = styled.div<{ $h?: number; $borderless?: boolean; $interactive?: boolean }>`
  flex: 1;
  min-width: 0;
  height: ${({ $h }) => $h || 22}px;
  border-radius: 99px;
  background: ${BAR_TRACK_BG};
  border: ${({ $borderless }) =>
    $borderless ? "none" : `1px solid ${theme.colors.grey2}`};
  overflow: hidden;
  cursor: ${({ $interactive }) => ($interactive ? "help" : "default")};
`;

const Fill = styled.div<{ $width: string; $color: string }>`
  display: block;
  width: ${({ $width }) => $width};
  height: 100%;
  border-radius: 99px;
  background: ${({ $color }) => $color};
`;

const BarMeta = styled.span`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 4px;
  justify-self: start;
  text-align: left;
  white-space: nowrap;
  flex: none;

  strong {
    font-size: 16px;
    line-height: 1;
    color: #000;
  }

  span {
    font-size: 16px;
    line-height: 1;
    color: #000;
  }
`;

const Footnote = styled.span`
  font-size: 14px;
  line-height: 1.4;
  color: ${theme.colors.grey1};
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 26px;
  align-items: stretch;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }

  & > div {
    height: 100%;
  }
`;

const ChartVisual = styled.div`
  height: ${CHART_VISUAL_SIZE}px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
`;

const Pie = styled.div<{ $bg: string }>`
  width: ${CHART_VISUAL_SIZE}px;
  height: ${CHART_VISUAL_SIZE}px;
  border-radius: 50%;
  flex: none;
  background: ${({ $bg }) => $bg};
`;

const PieLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${CHANNEL_ROW_GAP}px;
  width: 100%;
`;

const LegendLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: ${CHANNEL_ROW_HEIGHT}px;
  font-size: 16px;
  line-height: 1;
  color: #000;
  white-space: nowrap;

  strong {
    width: 44px;
    text-align: right;
    font-weight: 400;
    flex: none;
  }
`;

const Muted = styled.span`
  flex: 1;
  font-size: 14px;
  color: ${theme.colors.grey1};
  text-align: right;
`;

const Dot = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex: none;
`;

const SendHero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const SendHeroValue = styled(KpiValue)`
  font-size: 48px;
`;

const SendValue = styled.span`
  justify-self: start;
  text-align: left;
  font-size: 16px;
  line-height: 1;
  color: #000;
  flex: none;
  white-space: nowrap;
`;

const MetricBarList = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr) auto;
  column-gap: 16px;
  row-gap: ${CHANNEL_ROW_GAP}px;
  align-items: center;

  ${BarLabel} {
    width: auto;
    white-space: nowrap;
    min-height: ${CHANNEL_ROW_HEIGHT}px;
  }

  @media (max-width: 640px) {
    grid-template-columns: max-content minmax(0, 1fr);

    ${BarLabel} {
      width: auto;
    }

    ${BarMeta}, ${SendValue} {
      grid-column: 2;
      justify-self: start;
    }
  }
`;

const DistList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 12px;
`;

const DistHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  ${BarLabel} {
    width: auto;
    white-space: nowrap;
  }
`;

const Stacked = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  height: ${BAR_HEIGHT}px;
  border-radius: 99px;
  overflow: hidden;
  background: ${BAR_TRACK_BG};
`;

const StackSeg = styled.span<{ $width: string; $color: string }>`
  width: ${({ $width }) => $width};
  background: ${({ $color }) => $color};
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 14px;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 14px;
  line-height: 1;
  color: ${theme.colors.grey1};
`;

const DistRow = styled.div<{ $opacity?: number }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  opacity: ${({ $opacity }) => $opacity ?? 1};
`;

const TextLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 14px;
  line-height: 1;
  color: ${theme.colors.blue1};
  cursor: pointer;
`;

const AttentionRow = styled.div<{ $last?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0;
  cursor: pointer;
  border-bottom: 1px solid
    ${({ $last }) => ($last ? "transparent" : theme.colors.grey2)};

  .anticon {
    font-size: 20px;
    color: rgb(31, 31, 31);
  }

  .anticon-right {
    color: ${theme.colors.grey1};
  }
`;

const AttentionCopy = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;

  span:first-child {
    font-weight: 600;
    font-size: 16px;
    line-height: 1;
    color: #000;
  }
`;

const AttentionValue = styled.span`
  font-weight: 700;
  font-size: 16px;
  line-height: 1;
  color: #000;
`;

const UnlockedRow = styled.div`
  display: contents;
`;

const LockOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease;
`;

const LockWrap = styled.div<{ $row?: boolean }>`
  position: relative;
  min-width: 0;
  border-radius: 10px;
  height: ${({ $row }) => ($row ? "auto" : "100%")};

  ${({ $row }) =>
    $row &&
    css`
      grid-column: 1 / -1;
    `}

  &:hover ${LockOverlay} {
    opacity: 1;
    visibility: visible;
  }
`;

const LockBody = styled.div<{ $row?: boolean }>`
  height: 100%;
  filter: grayscale(1);
  opacity: 0.55;
  pointer-events: none;

  ${KpiCard} {
    height: 100%;
    background: #eef0f3;
    box-shadow: none;
  }

  ${KpiLabel},
  ${KpiValue},
  ${KpiNote},
  ${BarLabel},
  ${BarMeta},
  ${SendValue} {
    color: #9ca3af;
  }

  ${BarLabel} .anticon,
  ${KpiLabel} .anticon {
    color: #9ca3af;
  }

  ${({ $row }) =>
    $row &&
    css`
      display: grid;
      grid-template-columns: max-content minmax(0, 1fr) auto;
      column-gap: 16px;
      align-items: center;

      @media (max-width: 640px) {
        grid-template-columns: max-content minmax(0, 1fr);
      }
    `}
`;

const EnableLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  background: ${theme.colors.green1};
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(17, 24, 39, 0.16);

  &:hover {
    background: #047857;
    color: #fff;
  }
`;

const ChannelLock: React.FC<{
  locked: boolean;
  row?: boolean;
  action?: boolean;
  channelLabel: string;
  children: React.ReactNode;
}> = ({ locked, row, action = true, channelLabel, children }) => {
  const { t } = useTranslation();
  const [mailOpen, setMailOpen] = useState(false);
  if (!locked) {
    return row ? <UnlockedRow>{children}</UnlockedRow> : <>{children}</>;
  }
  if (!action) {
    return (
      <LockWrap $row={Boolean(row)}>
        <LockBody $row={Boolean(row)}>{children}</LockBody>
      </LockWrap>
    );
  }
  const href = `mailto:${CHANNEL_SUPPORT_MAIL}?subject=${encodeURIComponent(
    t("dashboard.v2.requestEnableSubject", { channel: channelLabel })
  )}`;
  const openMail = () => {
    window.location.href = href;
    setMailOpen(false);
  };
  return (
    <LockWrap $row={Boolean(row)}>
      <LockBody $row={Boolean(row)}>{children}</LockBody>
      <LockOverlay>
        <EnableLink
          href={href}
          onClick={(event) => {
            event.preventDefault();
            setMailOpen(true);
          }}
        >
          {t("dashboard.v2.requestEnable")}
        </EnableLink>
      </LockOverlay>
      <Modal
        open={mailOpen}
        title={t("dashboard.v2.requestEnable")}
        okText={t("dashboard.v2.requestEnableSend")}
        cancelText={t("common.cancel")}
        onOk={openMail}
        onCancel={() => setMailOpen(false)}
        destroyOnHidden
      >
        {t("dashboard.v2.requestEnableBody", {
          channel: channelLabel,
          email: CHANNEL_SUPPORT_MAIL,
        })}
      </Modal>
    </LockWrap>
  );
};

export default Dashboard;
